<script lang="ts">
    import { DEFAULT_LAYOUT } from '$lib/components/constants';

    import { getAllTrackingProperties } from '$lib/helper';
    import { isUrl } from '$lib/utils/isUrl';

    import get from 'lodash/get';
    import isEmpty from 'lodash/isEmpty';
    import { onMount, onDestroy } from 'svelte';

    import { CalendarProvider } from './constants';
    import Form from './FormQuestion.View.svelte';
    import { calendly } from '../_utils/calendly';
    import { CALENDLY_EVENT_SCHEDULED } from '../_utils/constants';
    import { sanitizeValues } from '../_utils/helpers';

    import type { ButtonStyle } from '$lib/types/Components/Button/ButtonContent';
    import type { InputData } from '$lib/types/Components/FormQuestion/InputData';
    import type { Box as BoxType, Align, Size } from '$lib/types/Components/SharedTypes';
    import type { FormValues } from '$lib/types/Form';
    import type { Language } from '$lib/types/Language';
    import type { OtpVerificationParams } from '$lib/types/OtpVerification';
    import type { Theme, TransitionEffect } from '$lib/types/Theme';
    import type { JSONContent } from '@tiptap/core';

    export let campaignId = '';

    // Calendly
    export let calendarUrl = '';
    export let provider = undefined;
    export let onCalendlyFailed = () => console.error('Calendly could not be loaded...');
    export let onCalendarFormSubmit: (formValues: FormValues) => void = () => null;
    export let otpVerificationParams: OtpVerificationParams | null = null;

    // Question
    export let id = '';
    export let theme: Theme;
    export let align: Align = 'center';
    export let draft: JSONContent;
    export let wysiwygHtml: string | undefined = undefined;
    export let color = 'black';
    export let box: BoxType;
    export let onSubmit: (formValues: FormValues) => void = () => null;
    export let onMouseEnter = () => null;
    export let layout = DEFAULT_LAYOUT;
    export let language: Language = 'en';
    export let transitionDelay = 0;
    export let transitionEffect: TransitionEffect = 'default';

    // Inputs
    export let inputs: InputData[] = [];

    // Submit Button
    export let btnId = '';
    export let btnDraft: JSONContent;
    export let btnWysiwygHtml: string | undefined = undefined;
    export let btnBgColor = '#0076FF';
    export let btnColor = 'white';
    export let btnAlign: Align = 'center';
    export let btnFullWidthOnDesktop: boolean = false;
    export let btnSize: Size = 'medium';
    export let btnStyle: ButtonStyle = 'filled';
    export let loading = false;

    let savedFormValues: FormValues = {};

    const isValidUrl =
        provider === CalendarProvider.CALENDLY
            ? isUrl(calendarUrl)
            : calendly.validateUrl(calendarUrl);

    onMount(() => {
        window.addEventListener('message', handleMessageEvent);
        calendly.enqueueScripts();
    });

    onDestroy(() => {
        window.removeEventListener('message', handleMessageEvent);

        if (!isEmpty(window.Calendly)) {
            window.Calendly.closePopupWidget();
        }
    });

    const openCalendlyPopup = (formData: FormValues) => {
        const Calendly = window.Calendly;

        if (isEmpty(Calendly)) {
            onCalendlyFailed();
            return;
        }

        const allTrackingProperties = getAllTrackingProperties(campaignId);

        const { namedAnswers, customAnswers } = calendly.extractCustomAnswers(
            inputs,
            formData,
            allTrackingProperties,
        );

        const calendlyConfig = calendly.buildConfig(
            calendarUrl,
            namedAnswers,
            customAnswers,
            allTrackingProperties,
        );

        Calendly.initPopupWidget(calendlyConfig);
    };

    const handleEventScheduled = () => {
        window.Calendly.closePopupWidget();

        onSubmit(savedFormValues);
    };

    const handleMessageEvent = (event: MessageEvent) => {
        const eventName = get(event, 'data.event', '');
        const isCalendlyEvent = eventName.includes('calendly');

        if (isCalendlyEvent && eventName === CALENDLY_EVENT_SCHEDULED) {
            handleEventScheduled();
        }
    };

    const handleSubmit = async (formData: FormValues) => {
        onCalendarFormSubmit(formData);

        // Open Calendly popup
        if (isValidUrl) {
            savedFormValues = formData;

            try {
                // Perform OTP verification before opening Calendly popup
                const { otpVerification, isNewPhoneField, isPhoneVerificationCodeField } =
                    otpVerificationParams || {};

                if (otpVerification) {
                    const sanitizedValues = sanitizeValues(
                        formData,
                        isNewPhoneField,
                        isPhoneVerificationCodeField,
                    );

                    await otpVerification(sanitizedValues);
                }

                return openCalendlyPopup(formData);
            } catch (error) {
                console.warn('OTP verification for Calendly form failed', { error });
                return;
            }
        }

        // Navigate to next page without Calendly popup
        return onSubmit(formData);
    };
</script>

<Form
    {id}
    {align}
    {draft}
    {wysiwygHtml}
    {color}
    {box}
    {inputs}
    {btnId}
    {btnDraft}
    {btnWysiwygHtml}
    {btnBgColor}
    {btnFullWidthOnDesktop}
    {btnColor}
    {btnAlign}
    {btnStyle}
    {btnSize}
    {loading}
    {layout}
    {language}
    {onMouseEnter}
    {transitionEffect}
    {transitionDelay}
    onSubmit={handleSubmit}
    {theme}
/>

<style>
    /* Workaround for the Calendly popup not showing the correct width on desktop devices */
    /* https://linear.app/perspective/issue/FUN-2050/fix-calendly-popup-display-on-desktop */
    /* Can potentially be removed in the future when Calendly implemented a fix on their side */
    :global(.calendly-overlay .calendly-popup) {
        max-width: 1105px !important;
    }
</style>
